<template>
  <a
    v-if="href"
    @click="$emit('click')"
    :class="computedClass"
    :href="href"
    :target="target">
    <BaseButtonContents
      :loading="loading"
      :leading-icon="leadingIcon"
      :trailing-icon="trailingIcon"
      :loader-color="computedLoaderColor">
      <slot></slot>
    </BaseButtonContents>
  </a>

  <router-link
    v-else-if="to"
    :class="computedClass"
    :to="to">
    <BaseButtonContents
      :loading="loading"
      :leading-icon="leadingIcon"
      :trailing-icon="trailingIcon"
      :loader-color="computedLoaderColor">
      <slot></slot>
    </BaseButtonContents>
  </router-link>

  <button
    v-else
    :class="computedClass"
    :type="type"
    :disabled="disabled"
    :loading="loading"
    @click="$emit('click')">
    <BaseButtonContents
      :loading="loading"
      :leading-icon="leadingIcon"
      :trailing-icon="trailingIcon"
      :loader-color="computedLoaderColor">
      <slot></slot>
    </BaseButtonContents>
  </button>
</template>

<script>
import BaseButtonContents from '@/components/BaseButtonContents.vue';

export default {
  name: 'BaseButton',

  components: {
    BaseButtonContents,
  },

  props: {
    shape:      { type: String,  default: 'rectangle' }, // pill, round, rectangle
    theme:      { type: String,  default: null }, // ghost, orange, green, red
    size:       { type: String,  default: 'medium' }, // small, medium, large
    outline:    { type: Boolean, default: false },
    fontNormal: { type: Boolean, default: false },
    block:      { type: Boolean, default: false },
    href:       { type: String,  default: null },
    target:     { type: String,  default: null },
    type:       { type: String,  default: 'button' },
    disabled:   { type: Boolean, default: false },
    loading:    { type: Boolean, default: false },
    noHover:    { type: Boolean, default: false },
    noRadius:   { type: Boolean, default: false },

    leadingIcon:  { type: String, default: null },
    trailingIcon: { type: String, default: null },

    to: { type: [String, Object], default: null },
  },

  computed: {
    computedClass() {
      return [
        'BaseButton',
        this.shape,
        this.theme,
        this.size,
        { outline: this.outline },
        { block: this.block },
        { 'font-normal': this.fontNormal },
        { noHover: this.noHover },
        { noRadius: this.noRadius },
      ];
    },
    computedLoaderColor() {
      const whites = ['orange', 'red', 'green'];
      return (whites.includes(this.theme) ? 'white' : 'dark');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/_variables';

.BaseButton {
  $base-button-font-size: 14px;
  $base-button-line-height: 16px;

  display: inline-flex;
  position: relative;
  background: $white;
  font-size: $base-button-font-size;
  font-weight: bold;
  line-height: $base-button-line-height;
  text-align: center;
  align-items: center;
  border: 2px solid #888;
  &.small {
    $small-padding-y: ($button-height-small - ($base-button-line-height + 2)) / 2;
    min-height: $button-height-small;
    min-width:  $button-height-small;
    padding:    $small-padding-y 12px;
    font-size:  12px;
  }
  &.medium {
    $medium-padding-y: ($button-height-medium - ($base-button-line-height + 2)) / 2;
    min-height: $button-height-medium;
    min-width:  $button-height-medium;
    padding:    $medium-padding-y 16px;
  }
  &.pill {
    border-radius: 19px;
  }
  &.rectangle {
    border-radius: $radius;
  }
  &.round {
    border-radius: 50%;
  }
  &.round.medium {
    padding: 8px;
    font-size: 20px !important;
  }
  &.round.small {
    padding: 2px;
  }
  &.block {
    display: block;
    width: 100%;
  }
  &.ghost {
    border-color: transparent;
    background: transparent;
    &:not(.noHover):hover {
      background: rgba($dark-blue, .05);
    }
  }
  &.orange {
    color:        $orange;
    border-color: $orange;
    background:   $orange;
    &:not(.noHover):hover {
      background-color: darken($orange, 5%);
    }
  }
  &.green {
    color:        $green;
    border-color: $green;
    background:   $green;
  }
  &.red {
    color:        $red;
    border-color: $red;
    background:   $red;
  }
  &.orange:not(.outline),
  &.green:not(.outline),
  &.red:not(.outline) {
    color: $white;
  }
  &.outline {
    background: transparent;
  }
  &.font-normal {
    font-weight: normal;
  }
  &[loading] {
    color: rgba($white, 0);
  }
  &.noRadius {
    border-radius: 0 !important;
  }
}
</style>
